import { BadgeCard, BadgeCardRow } from '@bp/ui-components';
import { CSSProperties, memo, useMemo } from 'react';
import { averageColor } from '../../utils/average-color';
import { getCardTextColor } from '../../utils/getCardTextColor';
import { CardsInfoLesson } from '../Lessons/TimetableVersion/Table/TimetableVersionLessonsTable';

type LessonCardProps = {
  label: string;
  duration?: number | null;
  count: number;
  showHover?: boolean;
  gapSize?: number;
  showRounded?: boolean;
  lessonClasses?: CardsInfoLesson | null;
  teacherColors: string[];
  currentClassUuid?: string;
  isLoading?: boolean;
  style?: CSSProperties;
  className?: string;
};

export const LessonCard = memo(
  ({
    label,
    duration,
    count,
    showHover = true,
    gapSize = 3,
    showRounded = true,
    lessonClasses,
    teacherColors,
    isLoading,
    style,
    className,
  }: LessonCardProps) => {
    const width = useMemo(() => {
      return Math.abs(Math.min(Math.floor(duration ?? 0), 6)) as 1 | 2 | 3 | 4 | 5 | 6;
    }, [duration]);

    const showGap = !!lessonClasses?.usedDivision;

    const badgeCardRows: BadgeCardRow[] = useMemo(() => {
      const usedClassDivisionGroupUuids = lessonClasses ? lessonClasses?.groups.map((g) => g.uuid) : [];
      const division = lessonClasses?.usedDivision;
      const allDivisionGroups = division ? division.groups : null;

      const rows: BadgeCardRow[] = [];
      if (allDivisionGroups && allDivisionGroups.length > 0) {
        allDivisionGroups.forEach((divisionGroup) => {
          if (usedClassDivisionGroupUuids?.includes(divisionGroup.uuid)) {
            rows.push({
              label: divisionGroup.name,
              rowColor: 'var(--color-grey-light)',
              colColors: teacherColors,
            });
          } else {
            rows.push({
              label: '',
              rowColor: 'var(--color-grey-light)',
              colColors: [],
            });
          }
        });
      } else {
        rows.push({
          label: '',
          rowColor: 'var(--color-grey-light)',
          colColors: teacherColors,
        });
      }
      return rows;
    }, [lessonClasses, teacherColors]);

    // Ohne Lehrkraft-Farben ist der Kartenhintergrund hell (`--color-grey-light`) – der
    // Fail-safe auf dunklen Text ist hier also nicht nur sicher, sondern korrekt.
    const color = getCardTextColor(averageColor(teacherColors));

    return (
      <BadgeCard
        label={label}
        count={count}
        rows={badgeCardRows}
        width={width}
        showHover={showHover}
        showGap={showGap}
        gapSize={gapSize}
        showRounded={showRounded}
        style={style}
        isLoading={isLoading}
        className={className}
        color={color}
      />
    );
  },
);
LessonCard.displayName = 'LessonCard';

/**
 * Karten aus den noch generierten Karten-Abfragen tragen die Teilung als `groupsConnection.edges`;
 * die Karte erwartet die flache Form des Zuordnungs-Lesemodells.
 */
export const toCardsInfoLesson = (
  lessonClass:
    | {
        uuid: string;
        groups: { uuid: string; shortName: string }[];
        usedDivision?: {
          uuid: string;
          groupsConnection: { edges: Array<{ node: { uuid: string; name: string } }> };
        } | null;
      }
    | undefined,
): CardsInfoLesson | undefined =>
  lessonClass && {
    uuid: lessonClass.uuid,
    groups: lessonClass.groups,
    usedDivision: lessonClass.usedDivision && {
      uuid: lessonClass.usedDivision.uuid,
      groups: lessonClass.usedDivision.groupsConnection.edges.map((edge) => edge.node),
    },
  };
